import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { T, useT } from '../../../translation/src';
import { PropertiesSection, Property } from './PropertiesSection';
import { InputWithUnit } from '../../../components/inputs/Input/InputWithUnit';
import { ConvertType, useCondMeterToFeet, useCondFeetToMeter } from '../../../hooks/useFormatNumber';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';
import { getWallSketchNodesProperty, getWallSketchEdgesProperty, DEFAULT_SKELETON_HEIGHT_CONSTRAINT, SkeletonHeightBound, SkeletonNodeData, SkeletonEdgeData } from '@pointorama/potree-shared';
import { RendererContext } from '../../../contexts/RendererContext';
import { useFloorGroupsData } from '../../../hooks/potree/useFloorGroupsData';
import { BULGE_STRAIGHT_EPS, bulgeFromRadius, radiusFromBulge } from '../../../potree/utils/skeleton/arcGeometry';

interface WallSketchElementPropertiesProps {
  projectId: string;
  annotationId: string;
  kind: 'node' | 'edge';
  elementIds: string[];
}

interface FloorOption {
  groupIdentifier: string;
  name: string;
}

type BoundSide = 'top' | 'bottom';

/** Select value for a bound's reference mode. */
const modeKey = (bound: SkeletonHeightBound): string => (bound.mode === 'default' ? 'default' : `floor:${bound.mode.targetFloorId}`);

/** The value every item shares, or null when the selection is mixed (or empty). */
function commonValue<V>(values: readonly V[]): V | null {
  if (values.length === 0) return null;
  return values.every((v) => v === values[0]) ? values[0] : null;
}

/**
 * Live-updating numeric input state (no confirm buttons): edits save on every
 * parseable keystroke; incomplete input ("", "-") stays local until it parses.
 * External changes (other user, undo, element switch) reset the text — unless
 * they're a numerically-equal echo of the user's own in-progress edit (e.g.
 * "1.5" coming back formatted as "1.50"). An empty `display` (mixed values in
 * a multi-selection) is never treated as numeric 0. State is adjusted during
 * render — the pattern react-hooks/set-state-in-effect recommends over effects.
 */
function useLiveNumberInput(display: string, save: (parsed: number) => void): [string, (event: React.ChangeEvent<HTMLInputElement>) => void] {
  const [text, setText] = useState(display);
  const [prevDisplay, setPrevDisplay] = useState(display);
  if (prevDisplay !== display) {
    setPrevDisplay(display);
    const isEcho = display !== '' && text.trim() !== '' && Number(text) === Number(display);
    if (!isEcho) setText(display);
  }
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setText(raw);
    const parsed = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(parsed)) return;
    save(parsed);
  };
  return [text, onChange];
}

/**
 * Mode <select> + offset input for one height bound (top or bottom) across
 * the selected elements. Mixed values show empty; each control only patches
 * its own field, so e.g. setting the offset keeps every element's reference.
 */
const BoundEditor: React.FC2<{
  idPrefix: string;
  bounds: SkeletonHeightBound[];
  defaultLabel: string;
  mixedLabel: string;
  floors: FloorOption[];
  onChange: (patch: Partial<SkeletonHeightBound>) => void;
}> = ({ idPrefix, bounds, defaultLabel, mixedLabel, floors, onChange }) => {
  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  const modeValue = commonValue(bounds.map(modeKey));
  const offset = commonValue(bounds.map((b) => b.offset));
  const offsetDisplay = offset === null ? '' : condMeterToFeet(offset).toFixed(2);
  const [offsetText, handleOffsetChange] = useLiveNumberInput(offsetDisplay, (parsed) => onChange({ offset: condFeetToMeter(parsed) }));

  return (
    <>
      <Property title={<T _str="reference" swc />}>
        <select
          className="w-full bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:text-white"
          value={modeValue ?? 'mixed'}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === 'default') onChange({ mode: 'default' });
            else if (raw.startsWith('floor:')) onChange({ mode: { targetFloorId: raw.slice('floor:'.length) } });
          }}
        >
          {modeValue === null && (
            <option value="mixed" disabled hidden>
              {mixedLabel}
            </option>
          )}
          <option value="default">{defaultLabel}</option>
          {floors.map((g) => (
            <option key={g.groupIdentifier} value={`floor:${g.groupIdentifier}`}>
              {g.name}
            </option>
          ))}
        </select>
      </Property>
      <Property title={<T _str="offset" swc />}>
        <InputWithUnit id={`${idPrefix}-offset`} value={offsetText} placeholder={offset === null ? mixedLabel : undefined} onChange={handleOffsetChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
      </Property>
    </>
  );
};

/**
 * Right-panel view shown while skeleton nodes or edges are selected in the
 * layout editor. Temporarily replaces the skeleton summary with the
 * elements' height constraints (independent upper + lower bound) and, for
 * edges, their wall thicknesses + type. With several elements selected every
 * edit applies to all of them; curvature stays single-edge only (a radius is
 * relative to each wall's own chord).
 *
 * While a wall-sketch edit session is editing this sketch, the values shown
 * come from the session's WORKING scene skeleton (live, uncommitted — follows
 * every canvas drag), and edits are applied through the session so they land
 * in its undo stack, commit with Enter and revert with Esc. Outside a
 * session, values come from the persisted properties and edits dispatch
 * `edit_skeleton` to persist immediately.
 */
const WallSketchElementProperties_: React.FC2<WallSketchElementPropertiesProps> = ({ projectId, annotationId, kind, elementIds }) => {
  const { viewer } = useContext(RendererContext);
  const persistedNodes = usePropertyValue(getWallSketchNodesProperty(annotationId));
  const persistedEdges = usePropertyValue(getWallSketchEdgesProperty(annotationId));

  const sceneSketch = viewer?.scene.wallSketches.find((s) => s.identifier === annotationId) ?? null;
  const sessionActive = viewer?.activeWallSketchEditSession?.getEditedSketchId() === annotationId;

  // Re-render on every working-data change (setData) so the panel follows
  // canvas edits — width-handle drags, node moves, bow drags — live.
  const [, setDataVersion] = useState(0);
  useEffect(() => {
    if (!sceneSketch) return;
    return sceneSketch.subscribeDataChanged(() => setDataVersion((v) => v + 1));
  }, [sceneSketch]);

  const nodes = sessionActive && sceneSketch ? sceneSketch.getNodes() : persistedNodes;
  const edges = sessionActive && sceneSketch ? sceneSketch.getAllEdges() : persistedEdges;
  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();
  const { loadFloorGroupsData } = useFloorGroupsData({ id: projectId });
  const floorGroups = useMemo(() => loadFloorGroupsData(), [loadFloorGroupsData]);
  // Floor height is owned by the sketch's floor group, not the sketch itself.
  // The scene object derives it from the floor plane; read it there.
  const floorHeight = sceneSketch?.getFloorHeight() ?? 0;
  const topDefaultLabel = useT('default (ceiling)', { swc: true });
  const bottomDefaultLabel = useT('default (this floor)', { swc: true });
  const mixedLabel = useT('mixed', { swc: true });
  const leftLabel = useT('left', { swc: true });
  const rightLabel = useT('right', { swc: true });

  const idSet = new Set(elementIds);
  const selectedNodes = kind === 'node' ? (nodes ?? []).filter((n) => idSet.has(n.id)) : [];
  const selectedEdges = kind === 'edge' ? (edges ?? []).filter((e) => idSet.has(e.id)) : [];
  const selected: Array<SkeletonNodeData | SkeletonEdgeData> = kind === 'node' ? selectedNodes : selectedEdges;
  const constraints = selected.map((el) => el.height ?? DEFAULT_SKELETON_HEIGHT_CONSTRAINT);
  // Curvature is only editable on a lone edge.
  const edge = selectedEdges.length === 1 ? selectedEdges[0] : undefined;

  // Apply a mutated nodes/edges array: through the active edit session's
  // WORKING state when one is editing this sketch (undoable, Enter commits,
  // Esc reverts), otherwise via the persisting `edit_skeleton` path.
  const persist = useCallback(
    (nextNodes: SkeletonNodeData[], nextEdges: SkeletonEdgeData[]) => {
      if (!viewer) return;
      const session = viewer.activeWallSketchEditSession;
      if (session?.getEditedSketchId() === annotationId && session.applyPanelEdit(nextNodes, nextEdges)) return;
      viewer.scene.dispatchEvent({
        type: 'edit_skeleton',
        value: { annotationIdentifier: annotationId, nodes: nextNodes, edges: nextEdges },
      });
    },
    [viewer, annotationId],
  );

  // Handlers read the CURRENT data at call time — the working skeleton
  // mutates in place during a session, so arrays captured at render time
  // could be stale by the time the user confirms an input. Plain functions
  // (no useCallback): the data source is mutable, so manual memoization
  // can't be preserved by the React Compiler anyway.
  const readCurrentData = (): { nodes: SkeletonNodeData[]; edges: SkeletonEdgeData[] } | null => {
    if (sessionActive && sceneSketch) return { nodes: sceneSketch.getNodes(), edges: sceneSketch.getAllEdges() };
    if (!persistedNodes || !persistedEdges) return null;
    return { nodes: persistedNodes, edges: persistedEdges };
  };

  /** Patch one height bound on every selected element, keeping its other fields. */
  const updateBound = (side: BoundSide, patch: Partial<SkeletonHeightBound>): void => {
    const data = readCurrentData();
    if (!data) return;
    const patchHeight = <E extends SkeletonNodeData | SkeletonEdgeData>(el: E): E => {
      if (!idSet.has(el.id)) return el;
      const current = el.height ?? DEFAULT_SKELETON_HEIGHT_CONSTRAINT;
      return { ...el, height: { ...current, [side]: { ...current[side], ...patch } } };
    };
    if (kind === 'node') persist(data.nodes.map(patchHeight), data.edges);
    else persist(data.nodes, data.edges.map(patchHeight));
  };

  const updateEdgeSize = (side: 'leftSize' | 'rightSize', meters: number): void => {
    const data = readCurrentData();
    if (!data) return;
    persist(
      data.nodes,
      data.edges.map((e) => (idSet.has(e.id) ? { ...e, [side]: meters } : e)),
    );
  };

  const updateEdgeBulge = (bulge: number | undefined): void => {
    const data = readCurrentData();
    if (!data || !edge) return;
    persist(
      data.nodes,
      data.edges.map((e) => (e.id === edge.id ? { ...e, bulge } : e)),
    );
  };

  const otherFloors = useMemo<FloorOption[]>(
    () => floorGroups.filter((g) => Math.abs(g.floor.height - floorHeight) > 1e-3).map((g) => ({ groupIdentifier: g.groupIdentifier, name: g.name })),
    [floorGroups, floorHeight],
  );

  // Curvature of the selected edge: radius of its arc (0 = straight) + the
  // side it bows toward. Radius edits keep the current side; below the
  // geometric minimum (half the chord) the value clamps to a semicircle.
  const edgeStartNode = edge ? nodes?.find((n) => n.id === edge.startNodeId) : undefined;
  const edgeEndNode = edge ? nodes?.find((n) => n.id === edge.endNodeId) : undefined;
  const chordLength = edgeStartNode && edgeEndNode ? Math.hypot(edgeEndNode.position.x - edgeStartNode.position.x, edgeEndNode.position.y - edgeStartNode.position.y) : 0;
  const bulge = edge?.bulge ?? 0;
  const isCurved = Math.abs(bulge) >= BULGE_STRAIGHT_EPS;
  const radiusMeters = isCurved ? radiusFromBulge(chordLength, bulge) : 0;
  const radiusDisplay = isCurved ? condMeterToFeet(radiusMeters).toFixed(2) : '0';

  const leftSize = commonValue(selectedEdges.map((e) => e.leftSize));
  const rightSize = commonValue(selectedEdges.map((e) => e.rightSize));
  const wallType = commonValue(selectedEdges.map((e) => e.wallType));
  const [leftSizeText, handleLeftSizeChange] = useLiveNumberInput(leftSize === null ? '' : condMeterToFeet(leftSize).toFixed(3), (parsed) => updateEdgeSize('leftSize', condFeetToMeter(parsed)));
  const [rightSizeText, handleRightSizeChange] = useLiveNumberInput(rightSize === null ? '' : condMeterToFeet(rightSize).toFixed(3), (parsed) => updateEdgeSize('rightSize', condFeetToMeter(parsed)));
  const [radiusText, handleRadiusChange] = useLiveNumberInput(radiusDisplay, (parsed) => {
    const meters = condFeetToMeter(parsed);
    if (!(meters > 0) || !isFinite(meters)) {
      updateEdgeBulge(undefined); // 0 → straight
      return;
    }
    // A radius below half the chord would clamp to a semicircle and the echo
    // would clobber in-progress typing (e.g. the "1" of "15") — leave those
    // local until the value grows past the geometric minimum.
    if (meters < chordLength / 2) return;
    const sign = bulge < 0 ? -1 : 1; // keep the current bow side
    updateEdgeBulge(bulgeFromRadius(chordLength, meters, sign));
  });

  if (selected.length === 0) return null;
  const count = selected.length;

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          {kind === 'edge' ? (
            count === 1 ? (
              <T _str="wall" swc />
            ) : (
              <T _str="{count} walls" args={{ count }} swc />
            )
          ) : count === 1 ? (
            <T _str="junction" swc />
          ) : (
            <T _str="{count} junctions" args={{ count }} swc />
          )}
        </Property>
        {kind === 'edge' && (
          <Property title={<T _str="wall type" swc />}>
            <span className="dark:text-white">{wallType ?? mixedLabel}</span>
          </Property>
        )}
      </PropertiesSection>

      <PropertiesSection title={<T _str="top" swc />}>
        <BoundEditor idPrefix="skeleton-top" bounds={constraints.map((c) => c.top)} defaultLabel={topDefaultLabel} mixedLabel={mixedLabel} floors={otherFloors} onChange={(patch) => updateBound('top', patch)} />
      </PropertiesSection>

      <PropertiesSection title={<T _str="bottom" swc />}>
        <BoundEditor idPrefix="skeleton-bottom" bounds={constraints.map((c) => c.bottom)} defaultLabel={bottomDefaultLabel} mixedLabel={mixedLabel} floors={otherFloors} onChange={(patch) => updateBound('bottom', patch)} />
      </PropertiesSection>

      {kind === 'edge' && (
        <PropertiesSection title={<T _str="thickness" swc />}>
          <Property title={<T _str="left" swc />}>
            <InputWithUnit id="skeleton-left-size" value={leftSizeText} placeholder={leftSize === null ? mixedLabel : undefined} onChange={handleLeftSizeChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
          </Property>
          <Property title={<T _str="right" swc />}>
            <InputWithUnit id="skeleton-right-size" value={rightSizeText} placeholder={rightSize === null ? mixedLabel : undefined} onChange={handleRightSizeChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
          </Property>
        </PropertiesSection>
      )}

      {edge && (
        <PropertiesSection title={<T _str="curvature" swc />}>
          <Property title={<T _str="radius" swc />}>
            <InputWithUnit id="skeleton-curve-radius" value={radiusText} onChange={handleRadiusChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
          </Property>
          {isCurved && (
            <Property title={<T _str="bows toward" swc />}>
              <select
                className="w-full bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:text-white"
                value={bulge > 0 ? 'left' : 'right'}
                onChange={(e) => {
                  const wantLeft = e.target.value === 'left';
                  if (wantLeft !== bulge > 0) updateEdgeBulge(-bulge);
                }}
              >
                <option value="left">{leftLabel}</option>
                <option value="right">{rightLabel}</option>
              </select>
            </Property>
          )}
        </PropertiesSection>
      )}
    </div>
  );
};

export const WallSketchElementProperties = memo(WallSketchElementProperties_);
